import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { DESKTOP_BREAKPOINT, PANEL_TRANSITION_MS } from "./config";
import { toRectSnapshot, getOverlayTargetRect } from "./overlayAnimation";
import type { TonePanelOverlayState } from "../type/tonalTest";

export function useOverlayAnimation(isActiveStep: boolean) {
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => window.innerWidth >= DESKTOP_BREAKPOINT);
  const [tonePanelOverlay, setTonePanelOverlay] = useState<TonePanelOverlayState | null>(null);
  const overlayFrameRef = useRef<number | null>(null);
  const finishTransitionTimeoutRef = useRef<number | null>(null);
  const testPanelSlotRef = useRef<HTMLDivElement | null>(null);
  const testPanelShellRef = useRef<HTMLDivElement | null>(null);

  const clearOverlayFrame = useCallback(() => {
    if (overlayFrameRef.current !== null) {
      window.cancelAnimationFrame(overlayFrameRef.current);
      overlayFrameRef.current = null;
    }
  }, []);

  const clearFinishTransitionTimeout = useCallback(() => {
    if (finishTransitionTimeoutRef.current !== null) {
      window.clearTimeout(finishTransitionTimeoutRef.current);
      finishTransitionTimeoutRef.current = null;
    }
  }, []);

  const animateTonePanelToSource = (onComplete: () => void) => {
    if (!isDesktopLayout || tonePanelOverlay === null || testPanelSlotRef.current === null) {
      onComplete();
      return;
    }
    clearOverlayFrame();
    clearFinishTransitionTimeout();
    const nextSourceRect = toRectSnapshot(testPanelSlotRef.current.getBoundingClientRect());
    const nextTargetRect = getOverlayTargetRect();
    setTonePanelOverlay({
      sourceRect: nextSourceRect,
      targetRect: nextTargetRect,
      placeholderHeight: nextSourceRect.height,
      animateToTarget: false
    });
    finishTransitionTimeoutRef.current = window.setTimeout(() => {
      finishTransitionTimeoutRef.current = null;
      onComplete();
    }, PANEL_TRANSITION_MS);
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const handleMediaChange = (event: MediaQueryListEvent) => {
      setIsDesktopLayout(event.matches);
      if (!event.matches) {
        clearOverlayFrame();
        clearFinishTransitionTimeout();
        setTonePanelOverlay(null);
      }
    };
    mediaQuery.addEventListener("change", handleMediaChange);
    return () => mediaQuery.removeEventListener("change", handleMediaChange);
  }, [clearOverlayFrame, clearFinishTransitionTimeout]);

  useLayoutEffect(() => {
    if (!isActiveStep || !isDesktopLayout || tonePanelOverlay !== null || testPanelShellRef.current === null) {
      return;
    }
    const sourceRect = toRectSnapshot(testPanelShellRef.current.getBoundingClientRect());
    const targetRect = getOverlayTargetRect();
    setTonePanelOverlay({ sourceRect, targetRect, placeholderHeight: sourceRect.height, animateToTarget: false });
    overlayFrameRef.current = window.requestAnimationFrame(() => {
      overlayFrameRef.current = null;
      setTonePanelOverlay(current => (current === null ? current : { ...current, animateToTarget: true }));
    });
  }, [isDesktopLayout, isActiveStep, tonePanelOverlay]);

  useEffect(() => {
    return () => {
      clearOverlayFrame();
      clearFinishTransitionTimeout();
    };
  }, [clearOverlayFrame, clearFinishTransitionTimeout]);

  const isTonePanelFloating = tonePanelOverlay !== null;
  const floatingTonePanelStyle: CSSProperties | undefined = tonePanelOverlay === null
    ? undefined
    : {
        top: `${tonePanelOverlay.targetRect.top}px`,
        left: `${tonePanelOverlay.targetRect.left}px`,
        width: `${tonePanelOverlay.targetRect.width}px`,
        height: `${tonePanelOverlay.targetRect.height}px`,
        maxHeight: "calc(100% - 150px)",
        transform: tonePanelOverlay.animateToTarget
          ? "translate3d(0, 0, 0) scale(1, 1)"
          : `translate3d(${tonePanelOverlay.sourceRect.left - tonePanelOverlay.targetRect.left}px, ${tonePanelOverlay.sourceRect.top - tonePanelOverlay.targetRect.top}px, 0) scale(${tonePanelOverlay.sourceRect.width / tonePanelOverlay.targetRect.width}, ${tonePanelOverlay.sourceRect.height / tonePanelOverlay.targetRect.height})`
      };

  return {
    tonePanelOverlay,
    setTonePanelOverlay,
    animateTonePanelToSource,
    clearOverlayFrame,
    clearFinishTransitionTimeout,
    testPanelSlotRef,
    testPanelShellRef,
    isTonePanelFloating,
    floatingTonePanelStyle,
  };
}
